import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { MagnifyingGlassIcon, XMarkIcon, BuildingOfficeIcon, ServerStackIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import managementService from '../api/managementService';
import type { Customer, Asset, Device, Gateway } from '../context/ManagementContext';

interface AdvancedEntitySelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (entity: Customer | Asset | Device | Gateway) => void;
  entityType: 'customer' | 'asset' | 'device' | 'gateway';
  title: string;
  selectedId?: string;
  customerFilter?: string; // For filtering assets/devices by customer
  assetFilter?: string; // For filtering devices by asset
}

export default function AdvancedEntitySelector({
  isOpen,
  onClose,
  onSelect,
  entityType,
  title,
  selectedId,
  customerFilter,
  assetFilter
}: AdvancedEntitySelectorProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [assetSearch, setAssetSearch] = useState('');
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState(customerFilter || '');
  const [selectedAssetFilter, setSelectedAssetFilter] = useState(assetFilter || '');
  
  // Debounced search terms
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce search terms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);


  // Fetch customers for filtering assets and devices (small initial set)
  const { data: customers = [] } = useQuery({
    queryKey: ['customers-selector-filter'],
    queryFn: () => managementService.customer.getAll({ 
      limit: 20
    }),
    select: (data) => data.data,
    enabled: (entityType === 'asset' || entityType === 'device') && isOpen,
  });

  // Fetch assets for filtering devices (small initial set)
  const { data: assets = [] } = useQuery({
    queryKey: ['assets-selector-filter', selectedCustomerFilter],
    queryFn: () => managementService.asset.getAll({ 
      limit: 20,
      customerId: selectedCustomerFilter || undefined
    }),
    select: (data) => data.data,
    enabled: entityType === 'device' && isOpen,
  });

  // Fetch entities based on type
  const { data: entitiesResponse, isLoading } = useQuery({
    queryKey: [
      `${entityType}-selector`,
      selectedCustomerFilter,
      selectedAssetFilter,
      debouncedSearchTerm
    ],
    queryFn: async () => {
      if (entityType === 'customer') {
        return managementService.customer.getAll({ 
          limit: 50,
          search: debouncedSearchTerm || undefined
        });
      } else if (entityType === 'asset') {
        return managementService.asset.getAll({ 
          limit: 50,
          customerId: selectedCustomerFilter || undefined,
          search: debouncedSearchTerm || undefined
        });
      } else if (entityType === 'device') {
        return managementService.device.getAll({ 
          limit: 50,
          customerId: selectedCustomerFilter || undefined,
          assetId: selectedAssetFilter || undefined,
          search: debouncedSearchTerm || undefined
        });
      } else {
        return managementService.gateway.getAll({ 
          limit: 50,
          customerId: selectedCustomerFilter || undefined,
          assetId: selectedAssetFilter || undefined,
          search: debouncedSearchTerm || undefined
        });
      }
    },
    enabled: isOpen,
  });

  const entities = entitiesResponse?.data || [];

  // Entities are already filtered server-side, so we don't need to filter them again
  const filteredEntities = entities;

  const handleSelect = (entity: Customer | Asset | Device | Gateway) => {
    onSelect(entity);
    onClose();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCustomerSearch('');
    setAssetSearch('');
    setSelectedCustomerFilter('');
    setSelectedAssetFilter('');
  };

  const getEntityIcon = (type: string) => {
    return type === 'customer' ? BuildingOfficeIcon : 
           type === 'asset' ? ServerStackIcon : 
           type === 'device' ? CpuChipIcon : CpuChipIcon;
  };

  const getEntityName = (entity: Customer | Asset | Device | Gateway) => {
    return entityType === 'customer' ? (entity as Customer).name : 
           entityType === 'asset' ? (entity as Asset).label : 
           entityType === 'device' ? (entity as Device).name : 
           (entity as Gateway).name || (entity as Gateway).id;
  };

  const getEntitySubtext = (entity: Customer | Asset | Device | Gateway) => {
    if (entityType === 'customer') {
      return (entity as Customer).description || 'No description';
    } else if (entityType === 'asset') {
      const asset = entity as Asset;
      const customer = customers.find(c => c.id === asset.customerId);
      return customer ? `Customer: ${customer.name}` : 'No customer assigned';
    } else if (entityType === 'device') {
      const device = entity as Device;
      const asset = assets.find(a => a.id === device.assetId);
      const customer = customers.find(c => c.id === device.customerId);
      return asset ? `Asset: ${asset.label}` : 
             customer ? `Customer: ${customer.name}` : 'No assignment';
    } else {
      const gateway = entity as Gateway;
      const customer = customers.find(c => c.id === gateway.customerId);
      return customer ? `Customer: ${customer.name}` : 
             gateway.isOnline ? 'Online' : 'Offline';
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-4xl bg-white rounded-xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <DialogTitle className="text-xl font-semibold text-gray-900">
              {title}
            </DialogTitle>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Search and Filters */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Main search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={`Search ${entityType}s...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Customer filter for assets and devices */}
              {(entityType === 'asset' || entityType === 'device') && (
                <>
                  <div>
                    <select
                      value={selectedCustomerFilter}
                      onChange={(e) => setSelectedCustomerFilter(e.target.value)}
                      className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Customers</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.id}>
                          {customer.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      disabled={!!selectedCustomerFilter}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    />
                  </div>
                </>
              )}

              {/* Asset filter for devices */}
              {entityType === 'device' && (
                <>
                  <div>
                    <select
                      value={selectedAssetFilter}
                      onChange={(e) => setSelectedAssetFilter(e.target.value)}
                      className="w-full py-3 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Assets</option>
                      {assets.map(asset => (
                        <option key={asset.id} value={asset.id}>
                          {asset.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search assets..."
                      value={assetSearch}
                      onChange={(e) => setAssetSearch(e.target.value)}
                      disabled={!!selectedAssetFilter}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                    />
                  </div>
                </>
              )}

              {/* Clear filters */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="mt-3 text-sm text-gray-600">
              {entitiesResponse?.pagination?.total || filteredEntities.length} {entityType}(s) found
            </div>
          </div>

          {/* Entity List */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredEntities.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {filteredEntities.map((entity: any) => {
                  const EntityIcon = getEntityIcon(entityType);
                  const isSelected = entity.id === selectedId;
                  
                  return (
                    <button
                      key={entity.id}
                      onClick={() => handleSelect(entity)}
                      className={`p-4 text-left rounded-lg border-2 transition-all hover:shadow-md ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`flex-shrink-0 p-2 rounded-lg ${
                          entityType === 'customer' ? 'bg-blue-100' : 
                          entityType === 'asset' ? 'bg-green-100' : 
                          entityType === 'device' ? 'bg-purple-100' : 'bg-orange-100'
                        }`}>
                          <EntityIcon className={`h-5 w-5 ${
                            entityType === 'customer' ? 'text-blue-600' : 
                            entityType === 'asset' ? 'text-green-600' : 
                            entityType === 'device' ? 'text-purple-600' : 'text-orange-600'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {getEntityName(entity)}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {getEntitySubtext(entity)}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            ID: {entity.id}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="flex-shrink-0">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className={`mx-auto h-12 w-12 rounded-full flex items-center justify-center ${
                  entityType === 'customer' ? 'bg-blue-100' : 
                  entityType === 'asset' ? 'bg-green-100' : 
                  entityType === 'device' ? 'bg-purple-100' : 'bg-orange-100'
                }`}>
                  {React.createElement(getEntityIcon(entityType), { 
                    className: `h-6 w-6 ${entityType === 'customer' ? 'text-blue-600' : 
                                         entityType === 'asset' ? 'text-green-600' : 
                                         entityType === 'device' ? 'text-purple-600' : 'text-orange-600'}` 
                  })}
                </div>
                <h3 className="mt-4 text-sm font-medium text-gray-900">
                  No {entityType}s found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try adjusting your search criteria or filters.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}